import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

const platformIcons = {
  Windows: "bi-windows",
  macOS: "bi-apple",
  Linux: "bi-tux",
  "Linux (deb)": "bi-tux",
  "Linux (arch)": "bi-tux",
};

const platformSlugMap = {
  Windows: "windows",
  macOS: "macos",
  Linux: "linux",
  "Linux (deb)": "linux-deb",
  "Linux (arch)": "linux-arch",
};

const platformDisplayOrder = [
  "Windows",
  "macOS",
  "Linux (deb)",
  "Linux (arch)",
  "Linux",
];

const BASE_PAGE_TITLE = "Download Profundus:AI | Detection-X";
const BASE_META_DESCRIPTION =
  "Download the latest Profundus:AI releases for Windows, macOS, and Linux from Detection-X's official download center.";

const HARDWARE_PROFILES = [
  {
    title: "Minimal annotation setup",
    items: [
      {
        label: "CPU",
        description: "Modern 4-core CPU (e.g., Intel i5 / Ryzen 5)",
      },
      {
        label: "RAM",
        description: "16 GB",
      },
      {
        label: "GPU",
        description:
          "Integrated graphics is sufficient as long as no training is running",
      },
      {
        label: "Storage",
        description:
          "512 GB SSD (preferably NVMe) minimum depending on dataset size",
      },
    ],
  },
  {
    title: "Recommended for productive training/inference",
    items: [
      {
        label: "CPU",
        description: "8+ cores (Ryzen 7 / Intel i7)",
      },
      {
        label: "RAM",
        description: "32 GB",
      },
      {
        label: "GPU",
        description: "NVIDIA RTX 3070/4070 (12 GB VRAM) or higher",
      },
      {
        label: "Storage",
        description: "At least 1 TB NVMe SSD",
      },
      {
        label: "Network",
        description: "Gigabit LAN",
      },
    ],
  },
  {
    title:
      "For heavy usage (parallel training of multiple models, large anomaly datasets, etc.)",
    items: [
      {
        label: "CPU",
        description: "16+ cores (Threadripper / Intel Xeon W) ",
      },
      {
        label: "RAM",
        description: "64 GB or more",
      },
      {
        label: "GPU",
        description:
          "At least an RTX 4090 (24 GB) or workstation Ampere (A5000/A6000) for high-resolution classification/segmentation projects or multiple parallel trainings",
      },
      {
        label: "Storage",
        description:
          "2 TB+ NVMe, plus optionally a separate SATA/NAS pool for archived data.",
      },
    ],
  },
];

function App() {
  const [products, setProducts] = useState([]);
  const [downloadsByProduct, setDownloadsByProduct] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productResponse, downloadsResponse] = await Promise.all([
          axios.get("/api/products"),
          axios.get("/api/downloads"),
        ]);

        setProducts(productResponse.data || []);
        setDownloadsByProduct(downloadsResponse.data || {});
      } catch (err) {
        setError("Failed to load download data");
        console.error("Error fetching downloads:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedProduct && products.length > 0) {
      const productWithDownloads = products.find((product) => {
        const productDownloads = downloadsByProduct[product.id] || {};
        return Object.keys(productDownloads).length > 0;
      });

      const initialProduct =
        productWithDownloads?.id ?? products[0]?.id ?? null;

      if (initialProduct) {
        setSelectedProduct(initialProduct);
      }
    }
  }, [products, downloadsByProduct, selectedProduct]);

  const selectedProductInfo =
    selectedProduct &&
    products.find((product) => product.id === selectedProduct);

  const selectedPlatformDownloads =
    (selectedProduct && downloadsByProduct[selectedProduct]) || {};

  const orderedPlatforms = useMemo(() => {
    const primary = platformDisplayOrder.filter(
      (platform) =>
        Array.isArray(selectedPlatformDownloads[platform]) &&
        selectedPlatformDownloads[platform].length > 0
    );

    const additional = Object.keys(selectedPlatformDownloads).filter(
      (platform) =>
        !platformDisplayOrder.includes(platform) &&
        Array.isArray(selectedPlatformDownloads[platform]) &&
        selectedPlatformDownloads[platform].length > 0
    );

    return [...primary, ...additional];
  }, [selectedPlatformDownloads]);

  const latestVersions = useMemo(() => {
    return orderedPlatforms.reduce((acc, platform) => {
      const versions = selectedPlatformDownloads[platform];
      if (versions && versions.length > 0) {
        acc[platform] = versions[0];
      }
      return acc;
    }, {});
  }, [orderedPlatforms, selectedPlatformDownloads]);

  useEffect(() => {
    const productName = selectedProductInfo?.name;
    const platformList = orderedPlatforms.length
      ? ` for ${orderedPlatforms.join(", ")}`
      : "";
    const computedTitle = productName
      ? `Download ${productName}${platformList} | Detection-X`
      : BASE_PAGE_TITLE;

    document.title = computedTitle;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        productName
          ? `Official Detection-X downloads for ${productName}${platformList}, complete with checksums and version history.`
          : BASE_META_DESCRIPTION
      );
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute("content", computedTitle);
    }

    const ogDescription = document.querySelector(
      'meta[property="og:description"]'
    );
    if (ogDescription) {
      ogDescription.setAttribute(
        "content",
        productName
          ? `Download ${productName}${platformList} from Detection-X's official download center.`
          : "Official Detection-X downloads for Profundus:AI across Windows, macOS, and Linux."
      );
    }

    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute("content", computedTitle);
    }

    const twitterDescription = document.querySelector(
      'meta[name="twitter:description"]'
    );
    if (twitterDescription) {
      twitterDescription.setAttribute(
        "content",
        productName
          ? `Grab the latest ${productName} downloads${platformList} with verified installers and SHA256 checksums.`
          : "Get Profundus:AI installers for Windows, macOS, and Linux directly from Detection-X."
      );
    }
  }, [orderedPlatforms, selectedProductInfo]);

  const getDirectDownloadUrl = (productSlug, platformLabel) => {
    const normalizedPlatform =
      latestVersions[platformLabel]?.platform_slug ||
      platformSlugMap[platformLabel] ||
      platformLabel.toLowerCase();
    return `${window.location.origin}/api/download/latest/${productSlug}/${normalizedPlatform}`;
  };

  const hasDownloads = orderedPlatforms.length > 0;

  return (
    <div className="App">
      {/* Header */}
      <header className="hero-section">
        <div className="container-fluid px-0">
          <div className="d-flex justify-content-between align-items-center px-3">
            <div>
              <h1 className="display-4 fw-bold mb-2">Download Profundus:AI</h1>
              <p className="lead mb-0">
                Official Detection-X download center for Profundus:AI installers
                on Windows, macOS, and Linux.
              </p>
            </div>
            <img
              src="/detection-x_light.svg"
              alt="Detection-X brand logo"
              width={350}
              className="me-4"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-5">
        {/* Loading State */}
        {loading && (
          <div className="text-center my-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading available downloads...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {/* Content */}
        {!loading && !error && selectedProductInfo && (
          <>
            <section className="mb-5" id="latest-downloads">
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
                <h2 className="section-heading mb-0">
                  Latest {selectedProductInfo.name} downloads
                </h2>
                <p className="text-muted mb-0">
                  {orderedPlatforms.length
                    ? `Download ${
                        selectedProductInfo.name
                      } for ${orderedPlatforms.join(", ")}.`
                    : `Download ${selectedProductInfo.name} as soon as builds are published.`}
                </p>
              </div>
              {hasDownloads ? (
                <div className="d-flex flex-wrap justify-content-center gap-4 mt-4">
                  {orderedPlatforms.map((platform) => (
                    <div
                      className="mb-4"
                      key={platform}
                      style={{ width: "320px" }}
                    >
                      <div className="card download-card latest-version shadow-sm">
                        <div className="card-body text-center">
                          <i
                            className={`bi ${
                              platformIcons[platform] || "bi-question-circle"
                            } platform-icon text-secondary`}
                          ></i>
                          <h3 className="card-title text-secondary">
                            {platform}
                          </h3>
                          <p className="card-text">
                            Version {latestVersions[platform]?.version ?? "N/A"}
                          </p>
                          <p className="card-text">
                            Released:{" "}
                            {latestVersions[platform]?.release_date ?? "N/A"}
                          </p>
                          <p className="card-text text-muted mb-3">
                            {latestVersions[platform]?.size_formatted ?? ""}
                          </p>
                          <a
                            href={latestVersions[platform]?.path}
                            className="btn btn-secondary mb-2"
                            download
                          >
                            <i className="bi bi-download me-2"></i>
                            Download
                          </a>
                          <div className="mt-2">
                            <small className="text-muted">Direct link:</small>
                            <br />
                            <div className="input-group input-group-sm mt-1">
                              <input
                                type="text"
                                className="form-control"
                                value={getDirectDownloadUrl(
                                  selectedProduct,
                                  platform
                                )}
                                readOnly
                                onClick={(e) => e.target.select()}
                                title="Click to select the direct download URL"
                                style={{ fontSize: "0.75rem" }}
                              />
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(
                                    getDirectDownloadUrl(
                                      selectedProduct,
                                      platform
                                    )
                                  );
                                }}
                                title="Copy to clipboard"
                              >
                                <i className="bi bi-clipboard"></i>
                              </button>
                            </div>
                          </div>
                          <div className="mt-2">
                            <small className="text-muted">
                              SHA256 checksum:
                            </small>
                            <br />
                            <div className="input-group input-group-sm mt-1">
                              <input
                                type="text"
                                className="form-control"
                                value={latestVersions[platform]?.sha256 ?? ""}
                                readOnly
                                onClick={(e) => e.target.select()}
                                title="Click to select the SHA256 checksum"
                                style={{ fontSize: "0.75rem" }}
                              />
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => {
                                  const checksum =
                                    latestVersions[platform]?.sha256;
                                  if (checksum) {
                                    navigator.clipboard.writeText(checksum);
                                  }
                                }}
                                title="Copy checksum to clipboard"
                                disabled={!latestVersions[platform]?.sha256}
                              >
                                <i className="bi bi-clipboard"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="alert alert-info mt-4" role="alert">
                  We have not published any downloads for{" "}
                  {selectedProductInfo.name} yet.
                </div>
              )}
            </section>

            <section className="mb-5" id="product-selector">
              <div className="d-flex flex-column flex-lg-row align-items-lg-start justify-content-between gap-4">
                <div>
                  <h2 className="section-heading">Select Product</h2>
                  <div
                    className="btn-group"
                    role="group"
                    aria-label="Product selection"
                  >
                    {products.map((product) => (
                      <button
                        type="button"
                        key={product.id}
                        className={`btn btn-outline-secondary ${
                          selectedProduct === product.id ? "active" : ""
                        }`}
                        onClick={() => setSelectedProduct(product.id)}
                      >
                        {product.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="card shadow-sm" style={{ maxWidth: "360px" }}>
                  <div className="card-body">
                    <h3 className="h5 mb-3">
                      System requirements for {selectedProductInfo.name}
                    </h3>
                    {selectedProductInfo.system_requirements?.length ? (
                      <ul className="list-unstyled mb-0 small">
                        {selectedProductInfo.system_requirements.map(
                          (req, idx) => (
                            <li
                              key={`${
                                selectedProductInfo.id || selectedProduct
                              }-${idx}`}
                              className="d-flex align-items-start mb-2"
                            >
                              <i className="bi bi-check2-circle text-success me-2 mt-1"></i>
                              <span>{req}</span>
                            </li>
                          )
                        )}
                      </ul>
                    ) : (
                      <p className="mb-0 text-muted small">
                        No requirements listed.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-5" id="hardware-specs">
              <div className="bg-white rounded shadow-sm p-4">
                <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-4">
                  <div>
                    <h2 className="section-heading mb-2">
                      Hardware-Anforderungen
                    </h2>
                  </div>
                </div>
                <div className="row g-4">
                  {HARDWARE_PROFILES.map((profile) => (
                    <div className="col-12 col-lg-4" key={profile.title}>
                      <div className="card h-100 shadow-sm">
                        <div className="card-body d-flex flex-column">
                          <h3 className="h5 mb-3">{profile.title}</h3>
                          <ul className="list-unstyled small mb-0">
                            {profile.items.map((item, index) => (
                              <li
                                key={`${profile.title}-${item.label}-${index}`}
                                className="mb-2"
                              >
                                <span className="fw-semibold">
                                  {item.label}:
                                </span>{" "}
                                <span>{item.description}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-light py-4">
        <div className="container text-center text-muted">
          <p>
            &copy; {new Date().getFullYear()} Detection-X. All rights reserved.
          </p>
          <p className="small">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      </footer>

      {/* Fixed Logo */}
      <a
        href="https://detection-x.de"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed-logo"
      >
        <div className="logo-reflection-wrapper">
          <img
            src="/x.svg"
            alt="Detection-X Logo"
            title="Click to visit Detection-X"
            className="logo-reflection-img"
          />
          <div className="logo-sheen"></div>
        </div>
      </a>
    </div>
  );
}

export default App;
